import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSubscription } from '../context/SubscriptionContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { 
  Check, 
  X, 
  AlertTriangle, 
  Loader2,
  Crown,
  Building2,
  Sparkles,
  Clock
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PricingPage = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const { subscription, fetchSubscription: refreshSubscription, isInTrial, getTrialDaysRemaining, isTrialExpired } = useSubscription();
  const [searchParams] = useSearchParams();
  const [isAnnual, setIsAnnual] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [currentPlan, setCurrentPlan] = useState(null);

  // Vérifier le retour de Stripe
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const success = searchParams.get('success');
    const cancelled = searchParams.get('cancelled');

    if (sessionId && success === 'true') {
      pollPaymentStatus(sessionId);
    } else if (cancelled === 'true') {
      toast.error('Paiement annulé');
    }
  }, [searchParams]);

  // Synchroniser avec le contexte d'abonnement
  useEffect(() => {
    if (subscription) {
      setCurrentPlan(subscription);
    }
  }, [subscription]);

  // Charger l'abonnement actuel
  useEffect(() => {
    const fetchSubscription = async () => {
      if (!token) return;
      try {
        const response = await axios.get(`${API_URL}/api/subscription`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCurrentPlan(response.data);
      } catch (error) {
        console.error('Erreur chargement abonnement:', error);
      }
    };
    fetchSubscription();
  }, [token]);

  // Polling du statut de paiement
  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    
    if (attempts >= maxAttempts) {
      toast.error('Vérification du paiement expirée. Veuillez vérifier votre email.');
      setCheckingPayment(false);
      return;
    }

    setCheckingPayment(true);

    try {
      const response = await axios.get(`${API_URL}/api/checkout/status/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.payment_status === 'paid') {
        toast.success('Paiement réussi ! Votre abonnement est activé.');
        setCheckingPayment(false);
        // Recharger l'abonnement
        const subResponse = await axios.get(`${API_URL}/api/subscription`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCurrentPlan(subResponse.data);
        // Nettoyer l'URL
        navigate('/pricing', { replace: true });
        return;
      }

      // Continuer le polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch (error) {
      console.error('Erreur vérification paiement:', error);
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    }
  };

  // Gérer le clic sur un plan
  const handleSelectPlan = async (planId) => {
    if (!token) {
      toast.info('Veuillez vous connecter pour souscrire');
      navigate('/login');
      return;
    }

    if (planId === 'free') {
      try {
        setLoading(true);
        await axios.post(`${API_URL}/api/subscription/activate-free`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Plan gratuit activé !');
        const response = await axios.get(`${API_URL}/api/subscription`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCurrentPlan(response.data);
      } catch (error) {
        toast.error('Erreur lors de l\'activation du plan gratuit');
      } finally {
        setLoading(false);
      }
      return;
    }

    // Pour les plans payants, créer une session Stripe
    try {
      setLoading(true);
      const fullPlanId = `${planId}_${isAnnual ? 'yearly' : 'monthly'}`;
      
      const response = await axios.post(`${API_URL}/api/checkout/create-session`, {
        plan_id: fullPlanId,
        origin_url: window.location.origin
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Rediriger vers Stripe Checkout
      window.location.href = response.data.url;
    } catch (error) {
      const message = error.response?.data?.detail || 'Erreur lors de la création du paiement';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  // Composant Feature
  const Feature = ({ included, text, warning = false }) => (
    <div className="flex items-center gap-3 py-1.5">
      {warning ? (
        <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0" />
      ) : included ? (
        <Check className="h-4 w-4 text-emerald-500 flex-shrink-0" />
      ) : (
        <X className="h-4 w-4 text-slate-300 flex-shrink-0" />
      )}
      <span className={`text-sm ${!included && !warning ? 'text-slate-400' : 'text-slate-600'}`}>
        {text}
      </span>
    </div>
  );

  // Affichage pendant le chargement du paiement
  if (checkingPayment) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-blue-600 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-slate-900">Vérification du paiement...</h2>
          <p className="text-slate-500 mt-2">Veuillez patienter pendant que nous confirmons votre paiement.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto py-8 px-4 animate-fade-in" data-testid="pricing-page">
      {/* Bannière d'état de l'essai */}
      {currentPlan?.plan_id === 'free' && (isInTrial() || isTrialExpired()) && (
        <div className={`mb-8 p-4 rounded-xl border ${
          isTrialExpired() 
            ? 'bg-amber-50 border-amber-200' 
            : 'bg-blue-50 border-blue-200'
        }`} data-testid="trial-status-banner">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-full ${isTrialExpired() ? 'bg-amber-100' : 'bg-blue-100'}`}>
              <Clock className={`h-5 w-5 ${isTrialExpired() ? 'text-amber-600' : 'text-blue-600'}`} />
            </div>
            <div>
              <h3 className={`font-medium ${isTrialExpired() ? 'text-amber-800' : 'text-blue-800'}`}>
                {isTrialExpired() 
                  ? 'Votre essai gratuit est terminé' 
                  : `Il vous reste ${getTrialDaysRemaining()} jour${getTrialDaysRemaining() > 1 ? 's' : ''} d'essai gratuit`
                }
              </h3>
              <p className="text-sm text-slate-600 mt-0.5">
                {isTrialExpired() 
                  ? 'Passez à Solo pour continuer à profiter de toutes les fonctionnalités sans restriction.' 
                  : 'Profitez de toutes les fonctionnalités pendant votre période d\'essai.'
                }
              </p>
              {currentPlan?.exports_remaining !== undefined && isTrialExpired() && (
                <p className="text-xs text-slate-500 mt-1">
                  {currentPlan.exports_remaining} export{currentPlan.exports_remaining !== 1 ? 's' : ''} restant{currentPlan.exports_remaining !== 1 ? 's' : ''} sur 12 cette année
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="text-center mb-12">
        <h1 className="font-chivo text-3xl md:text-4xl font-bold text-slate-900 mb-4">
          Choisissez votre plan
        </h1>
        <p className="text-slate-500 text-lg max-w-2xl mx-auto mb-8">
          Gérez votre comptabilité en toute simplicité avec le plan adapté à vos besoins.
        </p>

        {/* Toggle Mensuel/Annuel */}
        <div className="flex items-center justify-center gap-4">
          <span className={`text-sm font-medium ${!isAnnual ? 'text-slate-900' : 'text-slate-500'}`}>
            Mensuel
          </span>
          <Switch
            checked={isAnnual}
            onCheckedChange={setIsAnnual}
            data-testid="billing-toggle"
          />
          <span className={`text-sm font-medium ${isAnnual ? 'text-slate-900' : 'text-slate-500'}`}>
            Annuel
          </span>
          {isAnnual && (
            <Badge className="bg-emerald-100 text-emerald-700 border-0">
              -20%
            </Badge>
          )}
        </div>
      </div>

      {/* Plans Grid */}
      <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
        {/* Plan Gratuit */}
        <Card className="relative border-slate-200" data-testid="plan-free">
          <CardHeader className="pb-4">
            <Badge variant="outline" className="w-fit mb-2 text-slate-600 border-slate-300">
              GRATUIT
            </Badge>
            <CardTitle className="font-chivo text-xl">Essayer</CardTitle>
            <div className="mt-4">
              <span className="text-4xl font-bold text-slate-900">0€</span>
              <span className="text-slate-500 ml-2">pour toujours</span>
            </div>
          </CardHeader>
          <CardContent className="pt-4 border-t">
            <div className="space-y-1">
              <Feature included text="1 année de données" />
              <Feature included text="Exports limités (12/an)" />
              <Feature included text="Support FAQ" />
              <Feature warning text="Watermark sur PDF" />
              <Feature included={false} text="Support email" />
            </div>
          </CardContent>
          <CardFooter>
            <Button 
              variant="outline" 
              className="w-full"
              onClick={() => handleSelectPlan('free')}
              disabled={loading || currentPlan?.plan_id === 'free'}
              data-testid="select-free-btn"
            >
              {currentPlan?.plan_id === 'free' ? 'Plan actuel' : 'Commencer gratuitement'}
            </Button>
          </CardFooter>
        </Card>

        {/* Plan Solo - Mise en avant */}
        <Card className="relative border-2 border-blue-500 shadow-lg scale-[1.02]" data-testid="plan-solo">
          <div className="absolute -top-3 left-1/2 -translate-x-1/2">
            <Badge className="bg-blue-600 text-white px-4 py-1">
              <Sparkles className="h-3 w-3 mr-1" />
              POPULAIRE
            </Badge>
          </div>
          <CardHeader className="pb-4 pt-6">
            <CardTitle className="font-chivo text-xl flex items-center gap-2">
              <Crown className="h-5 w-5 text-blue-600" />
              Solo
            </CardTitle>
            <div className="mt-4">
              {isAnnual ? (
                <>
                  <span className="text-4xl font-bold text-slate-900">99,00€</span>
                  <span className="text-slate-500 ml-2">par an</span>
                  <p className="text-sm text-emerald-600 mt-1">Économisez 20%</p>
                </>
              ) : (
                <>
                  <span className="text-4xl font-bold text-slate-900">9,90€</span>
                  <span className="text-slate-500 ml-2">par mois</span>
                </>
              )}
            </div>
          </CardHeader>
          <CardContent className="pt-4 border-t">
            <div className="space-y-1">
              <Feature included text="Historique illimité" />
              <Feature included text="Exports illimités" />
              <Feature included text="Support email (48h)" />
              <Feature included text="Sans watermark" />
              <Feature included text="1 entreprise" />
            </div>
          </CardContent>
          <CardFooter className="flex-col gap-2">
            <Button 
              className="w-full bg-blue-600 hover:bg-blue-700"
              onClick={() => handleSelectPlan('solo')}
              disabled={loading || currentPlan?.plan_id?.startsWith('solo')}
              data-testid="select-solo-btn"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : null}
              {currentPlan?.plan_id?.startsWith('solo') ? 'Plan actuel' : 'Commencer l\'essai gratuit'}
            </Button>
            <p className="text-xs text-slate-500 text-center">
              14 jours gratuits, sans carte bancaire
            </p>
          </CardFooter>
        </Card>

        {/* Plan Pro */}
        <Card className="relative border-slate-200" data-testid="plan-pro">
          <CardHeader className="pb-4">
            <Badge variant="outline" className="w-fit mb-2 text-purple-600 border-purple-300">
              <Building2 className="h-3 w-3 mr-1" />
              ENTREPRISE
            </Badge>
            <CardTitle className="font-chivo text-xl">Pro</CardTitle>
            <div className="mt-4">
              {isAnnual ? (
                <>
                  <span className="text-4xl font-bold text-slate-900">490,00€</span>
                  <span className="text-slate-500 ml-2">par an</span>
                  <p className="text-sm text-emerald-600 mt-1">Économisez 20%</p>
                </>
              ) : (
                <>
                  <span className="text-4xl font-bold text-slate-900">49,00€</span>
                  <span className="text-slate-500 ml-2">par mois</span>
                </>
              )}
            </div>
          </CardHeader>
          <CardContent className="pt-4 border-t">
            <div className="space-y-1">
              <Feature included text="Tout du plan Solo" />
              <Feature included text="Jusqu'à 50 entreprises" />
              <Feature included text="Multi-utilisateurs" />
              <Feature included text="Support prioritaire (24h)" />
              <Feature included text="API access" />
              <Feature included text="Export consolidé" />
            </div>
          </CardContent>
          <CardFooter>
            <Button 
              variant="outline"
              className="w-full border-purple-300 text-purple-700 hover:bg-purple-50"
              onClick={() => handleSelectPlan('pro')}
              disabled={loading || currentPlan?.plan_id?.startsWith('pro')}
              data-testid="select-pro-btn"
            >
              {currentPlan?.plan_id?.startsWith('pro') ? 'Plan actuel' : 'Démarrer maintenant'}
            </Button>
          </CardFooter>
        </Card>
      </div>

      {/* FAQ ou informations supplémentaires */}
      <div className="mt-16 text-center">
        <h2 className="font-chivo text-xl font-semibold text-slate-900 mb-4">
          Questions fréquentes
        </h2>
        <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto text-left">
          <div className="bg-slate-50 rounded-lg p-4">
            <h3 className="font-medium text-slate-900 mb-2">Puis-je changer de plan ?</h3>
            <p className="text-sm text-slate-600">
              Oui, vous pouvez passer à un plan supérieur à tout moment. La différence sera calculée au prorata.
            </p>
          </div>
          <div className="bg-slate-50 rounded-lg p-4">
            <h3 className="font-medium text-slate-900 mb-2">Comment fonctionne l'essai gratuit ?</h3>
            <p className="text-sm text-slate-600">
              Vous avez 14 jours pour tester toutes les fonctionnalités. Aucune carte bancaire requise.
            </p>
          </div>
          <div className="bg-slate-50 rounded-lg p-4">
            <h3 className="font-medium text-slate-900 mb-2">Puis-je annuler à tout moment ?</h3>
            <p className="text-sm text-slate-600">
              Oui, vous pouvez annuler votre abonnement à tout moment depuis votre espace de facturation.
            </p>
          </div>
          <div className="bg-slate-50 rounded-lg p-4">
            <h3 className="font-medium text-slate-900 mb-2">Mes données sont-elles sécurisées ?</h3>
            <p className="text-sm text-slate-600">
              Absolument. Vos données sont chiffrées et stockées sur des serveurs sécurisés en Europe.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingPage;
